import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CardContent, Card, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import ManageBudgetTable from 'routes/Admin/Budget/BudgetManagement/BudgetList/ManageBudgetTable';
import { BudgetProposalWithResourcesT } from 'routes/BudgetModule/config/types';
import StyBoxAdmin from 'routes/Admin/shared/StyBoxAdmin';
import AddBudgetButton from './AddBudget/AddBudgetButton';
import CreateBPModal from './AddBudget/CreateBPModal';
import StyGridTitleContainer from '../../shared/StyGridTitleContainer';
import StyHeaderBudgetSettings from '../shared/StyHeaderBudgetSettings';
import {
    trackEventClickOnInitializeInscriptions,
    trackEventNewBudgetModaleOpened,
} from './trackersHelpers';
import { useParams } from 'react-router-dom';
import { BudgetBys } from 'business/constants';
import AuxiliaryTabs from 'components/SharedComponents/AuxiliaryTabs/AuxiliaryTabs';
import { BudgetSettingsUrlParams } from '../BudgetSettings';
import { translateBlockValue } from 'i18n';
import {
    GESTIONNAIRE_TABLE,
    ORG_CHART_FINANCE_TABLE,
} from 'routes/BudgetModule/config/constants';
import DuplicateSettingsModal from './DuplicateSettings/DuplicateSettingsModal';
import DuplicateArgumentsModal from './DuplicateArguments/DuplicateArgumentsModal';
import GroupActionButton from '../shared/GroupActionButton';
import Flex from 'ui/Flex/Flex';
import InitInscriptionsModal from './InitInscriptions/InitInscriptionsModal';
import CreateIcon from '@mui/icons-material/Create';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import { useGetInitialisationAvailable } from '../Initialisation/hook';
import { useGetBudgets } from '../hooks';

const StyContentWrapper = styled(Box, {
    name: 'StyContentWrapper',
})({
    height: '100%',
});

export default function ManageBudget({
    budgetProposals = [],
    isLoading = false,
}: {
    budgetProposals?: BudgetProposalWithResourcesT[];
    isLoading?: boolean;
}): React.ReactElement {
    const { t } = useTranslation();

    const { data: budgets } = useGetBudgets();
    const { data: initialisationAvailable } = useGetInitialisationAvailable();
    const { tab, by: selectedBy = 'gestionnaire.name' } =
        useParams<BudgetSettingsUrlParams>();

    const [showCreateBPModal, setShowCreateBPModal] = useState(false);
    const [showDuplicateSettingsModal, setDuplicateSettingsModal] =
        useState(false);
    const [showDuplicateArgumentsModal, setDuplicateArgumentsModal] =
        useState(false);
    const [showInitInscriptionsModal, setInitInscriptionsModal] =
        useState(false);

    const bps = useMemo(
        () => budgetProposals.filter((bp) => bp.by === selectedBy),
        [budgetProposals, selectedBy],
    );

    const createBPModal = (
        <CreateBPModal
            isModalShown={showCreateBPModal}
            budgetProposals={budgetProposals}
            setShowModal={setShowCreateBPModal}
            budgets={budgets ?? []}
        />
    );

    const duplicateSettingsModal = (
        <DuplicateSettingsModal
            isModalShown={showDuplicateSettingsModal}
            setShowModal={setDuplicateSettingsModal}
            budgetProposals={bps}
        />
    );

    const duplicateArgumentsModal = (
        <DuplicateArgumentsModal
            isModalShown={showDuplicateArgumentsModal}
            setShowModal={setDuplicateArgumentsModal}
            budgetProposals={bps}
        />
    );

    const initInscriptionsModal = (
        <InitInscriptionsModal
            isModalShown={showInitInscriptionsModal}
            setShowModal={setInitInscriptionsModal}
            budgetProposals={bps}
        />
    );

    const openCreateBPModal = () => {
        setShowCreateBPModal(true);
        trackEventNewBudgetModaleOpened();
    };

    const openDuplicateSettingsModal = () => {
        setDuplicateSettingsModal(true);
    };
    const openDuplicateArgumentsModal = () => {
        setDuplicateArgumentsModal(true);
    };
    const openInitInscriptionsModal = () => {
        setInitInscriptionsModal(true);
        trackEventClickOnInitializeInscriptions();
    };

    const children = (
        <>
            <StyHeaderBudgetSettings>
                <StyGridTitleContainer>
                    <Typography
                        variant="h4"
                        sx={{
                            color: (theme) => theme.palette.darkGrey,
                        }}
                    >
                        {t('adminBudgetSpace.budgetsList')}
                    </Typography>
                </StyGridTitleContainer>
            </StyHeaderBudgetSettings>
            <Flex
                alignItems="center"
                justifyContent="start"
                sx={{ margin: '1rem', gap: '1rem' }}
            >
                <GroupActionButton
                    title={t('adminBudgetSpace.duplicateSettings.title')}
                    onClick={openDuplicateSettingsModal}
                    icon={<FileCopyOutlinedIcon />}
                />
                <GroupActionButton
                    title={t('adminBudgetSpace.duplicateArguments.title')}
                    onClick={openDuplicateArgumentsModal}
                    icon={<FileCopyOutlinedIcon />}
                />
                <GroupActionButton
                    title={t('adminBudgetSpace.initInscriptions.button')}
                    onClick={openInitInscriptionsModal}
                    icon={<CreateIcon />}
                    enabled={initialisationAvailable || false}
                    tooltipTitle={t(
                        'adminBudgetSpace.initInscriptions.tooltip',
                    )}
                />
            </Flex>
            <StyContentWrapper>
                <Card
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '49em',
                    }}
                    elevation={1}
                >
                    <CardContent
                        sx={{
                            height: '100%',
                        }}
                    >
                        {/* We are using isFetching instead of isLoading otherwise
                gridApi is not defined in ActionCellRenderer on some scenario,
                This should be later fix later, probably in updataing data through
                react-query and not agGrid.
                */}
                        <ManageBudgetTable isLoading={isLoading} data={bps} />
                        {/* Doing as follow allows us to reset the state of our modal on close */}
                        {showCreateBPModal && createBPModal}
                        {showDuplicateSettingsModal && duplicateSettingsModal}
                        {showDuplicateArgumentsModal && duplicateArgumentsModal}
                        {showInitInscriptionsModal && initInscriptionsModal}
                    </CardContent>
                </Card>
            </StyContentWrapper>
        </>
    );

    const shouldDisplayTabs = bps.length !== budgetProposals.length;

    const tabsList = [
        {
            label: t('adminBudgetSpace.budgetProposalBy', {
                resource: translateBlockValue(GESTIONNAIRE_TABLE),
            }),
            pathname: `/budget_admin/${tab}/${BudgetBys.GESTIONNAIRE}`,
        },
        {
            label: t('adminBudgetSpace.budgetProposalBy', {
                resource: translateBlockValue(ORG_CHART_FINANCE_TABLE),
            }),
            pathname: `/budget_admin/${tab}/${BudgetBys.ORG_CHART_FINANCE}`,
        },
    ].map((item) => {
        return {
            ...item,
            key: item.label,
            tabActions: (
                <AddBudgetButton
                    openModal={openCreateBPModal}
                    sx={{ mr: '1rem' }}
                />
            ),

            children: children,
        };
    });

    return (
        <StyBoxAdmin>
            {shouldDisplayTabs && (
                <AuxiliaryTabs tabsList={tabsList} keepSearch={false} />
            )}
            {!shouldDisplayTabs && (
                <StyHeaderBudgetSettings>
                    <AddBudgetButton openModal={openCreateBPModal} />
                </StyHeaderBudgetSettings>
            )}
            {!shouldDisplayTabs && children}
        </StyBoxAdmin>
    );
}
